import type {
  GovernorLiveAction,
} from '@/lib/governorEventBus';

export type CallsWorkspaceEvidenceStatus =
  | 'attempt'
  | 'success'
  | 'failure'
  | 'waiting'
  | 'timeout'
  | 'ended'
  | 'unknown';

export type CallsWorkspaceLiveEvidenceItem =
  Readonly<{
    id: string;
    mode: 'LIVE';
    stage: string;
    status:
      CallsWorkspaceEvidenceStatus;
    source: string;
    reason: string | null;
    callSessionId: string | null;
    observedAt: string;
    blocking: boolean;
  }>;

export type CallsWorkspaceLiveEvidenceProjection =
  Readonly<{
    mode: 'LIVE';
    records:
      readonly CallsWorkspaceLiveEvidenceItem[];
    totalCount: number;
    successCount: number;
    failureCount: number;
    timeoutCount: number;
    waitingCount: number;
    blockerCount: number;
    latest:
      CallsWorkspaceLiveEvidenceItem | null;
    latestObservedAt: string | null;
    liveEvidenceAvailable: boolean;
    certificationAllowed: false;
    certificationReason: string;
  }>;

type UnknownRecord =
  Record<string, unknown>;

const asRecord = (
  value: unknown,
): UnknownRecord =>
  value !== null &&
  typeof value === 'object'
    ? value as UnknownRecord
    : {};

const asString = (
  value: unknown,
  fallback: string,
): string =>
  typeof value === 'string' &&
  value.trim().length > 0
    ? value
    : fallback;

const asNullableString = (
  value: unknown,
): string | null =>
  typeof value === 'string' &&
  value.trim().length > 0
    ? value
    : null;

const normalizeStatus = (
  value: unknown,
): CallsWorkspaceEvidenceStatus => {
  switch (value) {
    case 'attempt':
    case 'success':
    case 'failure':
    case 'waiting':
    case 'timeout':
    case 'ended':
      return value;

    default:
      return 'unknown';
  }
};

const isBlockingStatus = (
  status:
    CallsWorkspaceEvidenceStatus,
): boolean =>
  status === 'failure' ||
  status === 'timeout' ||
  status === 'waiting' ||
  status === 'unknown';

export function projectCallsWorkspaceLiveEvidence(
  actions:
    readonly GovernorLiveAction[],
): CallsWorkspaceLiveEvidenceProjection {
  const records =
    actions
      .filter(
        action =>
          action.type ===
          'governor_calls_runtime_event',
      )
      .slice(0, 40)
      .map(
        action => {
          const detail =
            asRecord(action.detail);

          const status =
            normalizeStatus(
              detail.status,
            );

          return Object.freeze({
            id: action.id,
            mode: 'LIVE' as const,
            stage: asString(
              detail.stage,
              'CALLS_RUNTIME_EVENT',
            ),
            status,
            source: asString(
              detail.source,
              'Calls Governor',
            ),
            reason:
              asNullableString(
                detail.reason,
              ) ??
              asNullableString(
                detail.error,
              ),
            callSessionId:
              asNullableString(
                detail.callSessionId,
              ) ??
              asNullableString(
                detail.callId,
              ),
            observedAt:
              asString(
                detail.timestamp,
                action.createdAt,
              ),
            blocking:
              isBlockingStatus(status),
          });
        },
      );

  const successCount =
    records.filter(
      record =>
        record.status === 'success' ||
        record.status === 'ended',
    ).length;

  const failureCount =
    records.filter(
      record =>
        record.status === 'failure',
    ).length;

  const timeoutCount =
    records.filter(
      record =>
        record.status === 'timeout',
    ).length;

  const waitingCount =
    records.filter(
      record =>
        record.status === 'waiting' ||
        record.status === 'unknown',
    ).length;

  const blockerCount =
    records.filter(
      record =>
        record.blocking,
    ).length;

  return Object.freeze({
    mode: 'LIVE',
    records:
      Object.freeze(records),
    totalCount:
      records.length,
    successCount,
    failureCount,
    timeoutCount,
    waitingCount,
    blockerCount,
    latest:
      records[0] ?? null,
    latestObservedAt:
      records[0]?.observedAt ??
      null,
    liveEvidenceAvailable:
      records.length > 0,
    certificationAllowed: false,
    certificationReason:
      records.length === 0
        ? 'No Calls live runtime evidence has been received.'
        : blockerCount > 0
          ? 'Live Calls evidence contains unresolved blockers.'
          : 'Runtime evidence exists, but correlated two-device certification is still required.',
  });
}
